import isEqual from 'lodash/isEqual';

import {
  type AIAppBlock,
  type AIChatMessageType,
  type AIMessageBlock,
  type ChatMessageType,
} from './aiType';

type StreamContent = {
  content: string;
  isComplete: boolean;
  sessionId?: string;
  requestId?: string;
};

type AppBlockInput = {
  resourceUri: string;
  toolName: string;
  structuredContent: unknown;
  sessionId?: string;
  requestId?: string;
};

const findInFlightAiMessageIndex = (messages: ChatMessageType[]) => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (message.role === 'ai' && !message.isComplete) {
      return index;
    }
  }

  return -1;
};

// Find the assistant message a websocket payload belongs to: prefer an exact requestId match,
// otherwise fall back to the in-progress assistant turn (covers payloads sent without a requestId).
const findTargetMessageIndex = (
  messages: ChatMessageType[],
  requestId: string | undefined,
): number => {
  if (requestId != null) {
    const matchedIndex = messages.findIndex(
      (message) => message.role === 'ai' && message.requestId === requestId,
    );

    if (matchedIndex !== -1) {
      return matchedIndex;
    }
  }

  return findInFlightAiMessageIndex(messages);
};

const appendTextBlock = (blocks: AIMessageBlock[], content: string): AIMessageBlock[] => {
  if (!content) {
    return blocks;
  }

  const lastBlock = blocks[blocks.length - 1];

  if (lastBlock?.type === 'text') {
    return [...blocks.slice(0, -1), { ...lastBlock, content: lastBlock.content + content }];
  }

  return [...blocks, { type: 'text', content }];
};

const replaceMessage = (
  messages: ChatMessageType[],
  index: number,
  message: AIChatMessageType,
): ChatMessageType[] => [...messages.slice(0, index), message, ...messages.slice(index + 1)];

export const appendStreamContentToMessages = (
  messages: ChatMessageType[],
  { content, isComplete, sessionId, requestId }: StreamContent,
): ChatMessageType[] => {
  const targetIndex = findTargetMessageIndex(messages, requestId);

  if (targetIndex === -1) {
    return [
      ...messages,
      {
        role: 'ai',
        blocks: appendTextBlock([], content),
        isComplete,
        sessionId,
        requestId,
      },
    ];
  }

  const target = messages[targetIndex] as AIChatMessageType;

  return replaceMessage(messages, targetIndex, {
    ...target,
    blocks: appendTextBlock(target.blocks, content),
    isComplete,
    sessionId: sessionId ?? target.sessionId,
    requestId: requestId ?? target.requestId,
  });
};

export const appendAppBlockToMessages = (
  messages: ChatMessageType[],
  { resourceUri, toolName, structuredContent, sessionId, requestId }: AppBlockInput,
): ChatMessageType[] => {
  const appBlock: AIAppBlock = { type: 'app', resourceUri, toolName, structuredContent };
  const targetIndex = findTargetMessageIndex(messages, requestId);

  if (targetIndex === -1) {
    return [
      ...messages,
      {
        role: 'ai',
        blocks: [appBlock],
        isComplete: false,
        sessionId,
        requestId,
      },
    ];
  }

  const target = messages[targetIndex] as AIChatMessageType;

  if (target.blocks.some((block) => block.type === 'app' && isEqual(block, appBlock))) {
    return messages;
  }

  return replaceMessage(messages, targetIndex, {
    ...target,
    blocks: [...target.blocks, appBlock],
    sessionId: sessionId ?? target.sessionId,
    requestId: requestId ?? target.requestId,
  });
};
